/**
 * Variant expansion for v2 mapping.
 *
 * Given a base ShopifyRow (the "product header" row) and a single VariantOption,
 * produce a new ShopifyRow with the variant-scoped overrides applied:
 *   - PRICE_LIKE_COLUMNS get `+priceDelta`
 *   - SKU_LIKE_COLUMNS   get `skuSuffix` appended
 *   - other applyTo columns get the raw variant.value written verbatim
 *
 * Also writes Option1 Name / Option1 Value.
 *
 * Pure functions. No DB, no IO.
 *
 * See docs/V2-PLAN.md §2 "Variant expansion".
 */

import type {
  ShopifyRow,
  VariantConfig,
  VariantDimension,
  VariantOption,
  VariantPriceAdj,
  VariantValue,
} from "./types";
import { PRICE_LIKE_COLUMNS, SKU_LIKE_COLUMNS } from "./columns";

/**
 * Add `delta` to a numeric price string.
 *
 * - Strips non-numeric characters from `base` (commas, currency, units) using
 *   the same logic as helpers.numberOnly.
 * - If the parsed sum is an integer, returns the integer string (no decimals).
 * - Otherwise returns a single-decimal-place string.
 * - If `base` is empty/non-numeric and `delta === 0`, returns `base` unchanged
 *   (so a blank price stays blank).
 * - If `base` is empty/non-numeric and `delta !== 0`, returns "" (caller may
 *   surface a warning).
 */
export function expandPriceLike(base: string, delta: number): string {
  const cleaned = numericOnly(base);
  if (cleaned.length === 0) {
    if (delta === 0) return base;
    return "";
  }
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) {
    if (delta === 0) return base;
    return "";
  }
  const sum = parsed + delta;
  if (Number.isInteger(sum)) return String(sum);
  // Single decimal place. Trim trailing zero (e.g. 6795.0 → "6795", 6795.5 → "6795.5").
  const fixed = sum.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}

/**
 * Append a SKU suffix to a base SKU. If base is empty, returns just the suffix.
 */
export function expandSkuLike(base: string, suffix: string): string {
  if (base == null) return suffix;
  return `${base}${suffix}`;
}

/**
 * Produce a new ShopifyRow representing a single variant.
 *
 * For each column in `applyTo`:
 *   - PRICE_LIKE_COLUMNS  → expandPriceLike(baseRow[col], variant.priceDelta)
 *   - SKU_LIKE_COLUMNS    → expandSkuLike(baseRow[col], variant.skuSuffix)
 *   - anything else       → variant.value (verbatim)
 *
 * Also sets Option1 Name (= optionName) and Option1 Value (= variant.value).
 *
 * Does not mutate `baseRow`.
 */
export function applyVariantOverride(
  baseRow: ShopifyRow,
  variant: VariantOption,
  optionName: string,
  applyTo: readonly string[],
): ShopifyRow {
  const next: ShopifyRow = { ...baseRow };

  for (const col of applyTo) {
    const current = next[col] ?? "";
    if ((PRICE_LIKE_COLUMNS as readonly string[]).includes(col)) {
      next[col] = expandPriceLike(current, variant.priceDelta);
    } else if ((SKU_LIKE_COLUMNS as readonly string[]).includes(col)) {
      next[col] = expandSkuLike(current, variant.skuSuffix);
    } else {
      next[col] = variant.value;
    }
  }

  next["Option1 Name"] = optionName;
  next["Option1 Value"] = variant.value;

  return next;
}

/**
 * Shopify's product CSV supports at most three option columns
 * (Option1..Option3 Name/Value). Dimensions beyond this are ignored.
 */
export const MAX_VARIANT_DIMENSIONS = 3;

/**
 * Resolve a VariantConfig into normalized dimensions. Legacy `{optionName,
 * options[]}` is upgraded to a single dimension; `dimensions` (when present)
 * wins. Returns [] when disabled or nothing is configured. At most
 * MAX_VARIANT_DIMENSIONS dimensions are returned (extras are ignored — see
 * `excessVariantDimensions` for the warning callers surface).
 */
export function normalizeVariants(cfg: VariantConfig | undefined): VariantDimension[] {
  if (!cfg || cfg.enabled !== true) return [];
  if (cfg.dimensions && cfg.dimensions.length > 0) {
    return cfg.dimensions.slice(0, MAX_VARIANT_DIMENSIONS);
  }
  if (cfg.optionName !== undefined && cfg.options && cfg.options.length > 0) {
    return [
      {
        name: cfg.optionName,
        values: cfg.options.map((o) => ({
          value: o.value,
          priceAdj: { mode: "fixed", amount: o.priceDelta },
          skuSuffix: o.skuSuffix,
        })),
      },
    ];
  }
  return [];
}

/**
 * Number of configured variant dimensions that `normalizeVariants` drops
 * because Shopify only supports three option columns. 0 when within limits
 * or variants are disabled.
 */
export function excessVariantDimensions(cfg: VariantConfig | undefined): number {
  if (!cfg || cfg.enabled !== true || !cfg.dimensions) return 0;
  return Math.max(0, cfg.dimensions.length - MAX_VARIANT_DIMENSIONS);
}

/** Cartesian product of dimension values, preserving dimension order. */
export function cartesian(dims: VariantDimension[]): VariantValue[][] {
  if (dims.length === 0) return [];
  let acc: VariantValue[][] = [[]];
  for (const dim of dims) {
    const next: VariantValue[][] = [];
    for (const prefix of acc) {
      for (const v of dim.values) next.push([...prefix, v]);
    }
    acc = next;
  }
  return acc;
}

/**
 * Apply an ordered list of price adjustments to a base price string.
 * - Strips currency/commas from base (numericOnly).
 * - "fixed" adds amount; "percent" adds amount% of the running value.
 * - Empty/non-numeric base with only zero/empty adjustments -> returns base unchanged.
 * - Empty/non-numeric base with a real adjustment -> "" (caller may warn).
 */
export function expandPriceWithAdjustments(
  base: string,
  adjustments: VariantPriceAdj[],
): string {
  const meaningful = adjustments.filter((a) => a.amount !== 0);
  const cleaned = numericOnly(base);
  if (cleaned.length === 0) return meaningful.length === 0 ? base : "";
  let running = Number(cleaned);
  if (!Number.isFinite(running)) return meaningful.length === 0 ? base : "";
  for (const adj of adjustments) {
    running = adj.mode === "percent" ? running + (running * adj.amount) / 100 : running + adj.amount;
  }
  if (Number.isInteger(running)) return String(running);
  return running.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * Apply a layered variant combo's price/sku/value overrides to a base row's
 * `applyTo` columns. Does NOT write Option/identity cells — that is the
 * variant encoder's job. Does not mutate baseRow.
 */
export function applyVariantValues(
  baseRow: ShopifyRow,
  combo: VariantValue[],
  applyTo: readonly string[],
  priceLike: readonly string[],
  skuLike: readonly string[],
): ShopifyRow {
  const next: ShopifyRow = { ...baseRow };
  const adjustments = combo
    .map((v) => v.priceAdj)
    .filter((a): a is VariantPriceAdj => a != null);
  const suffix = combo.map((v) => v.skuSuffix ?? "").join("");

  for (const col of applyTo) {
    const current = next[col] ?? "";
    if (priceLike.includes(col)) {
      next[col] = expandPriceWithAdjustments(current, adjustments);
    } else if (skuLike.includes(col)) {
      next[col] = `${current}${suffix}`;
    } else {
      next[col] = combo[combo.length - 1]?.value ?? "";
    }
  }
  return next;
}

/**
 * Shopify variant row: price/sku/value overrides + Option{n} Name/Value cells.
 * Does not mutate baseRow.
 */
export function applyLayeredVariant(
  baseRow: ShopifyRow,
  combo: VariantValue[],
  dimensionNames: string[],
  applyTo: readonly string[],
  priceLike: readonly string[],
  skuLike: readonly string[],
): ShopifyRow {
  const next = applyVariantValues(baseRow, combo, applyTo, priceLike, skuLike);
  for (let k = 0; k < combo.length; k++) {
    next[`Option${k + 1} Name`] = dimensionNames[k] ?? "";
    next[`Option${k + 1} Value`] = combo[k].value;
  }
  return next;
}

// ---------------------------------------------------------------------------
// internal
// ---------------------------------------------------------------------------

/** Same logic as helpers.numberOnly, inlined to keep variants.ts self-contained. */
function numericOnly(s: string): string {
  if (s == null) return "";
  const str = String(s);
  let result = "";
  let dotSeen = false;
  for (const ch of str) {
    if (ch >= "0" && ch <= "9") {
      result += ch;
    } else if (ch === "." && !dotSeen) {
      dotSeen = true;
      result += ch;
    }
  }
  return result;
}
